import * as React from "react";
import { useState, useEffect } from "react";
import { Accordion } from "../../../../../../../react-core/Html/Accordion";
import { Navigator } from "../../../../../../Shared/Wizards/Entities/Navigator";
import { SourceOfWealthOption } from "../../../../../../../Wrap/Constants/Enums/Apply/SourceOfWealthOption";
import { SubAccountType } from "../../../../../../../Wrap/Constants/Enums/Domain/SubAccountType";
import { ProcessType } from "../../../../../../Shared/IST/Resource/Content";
import { Typography } from "../../../../../../Shared/Typography/Typography";
import { Urls } from "../../../../../Urls";
import {
    DrawdownTypeMap,
    SourceOfWealthArrayMap,
    ThirdPartyTypeArrayMap,
} from "../../../NewBusinessWizard/Resource/Contents";
import {
    AnnuityPaymentsTypeOptionsMap,
    depositMethodArraysMap,
    DpcQuestionText,
    PaymentType,
    regularDepositMethodArraysMap,
    MpaaQuestionText,
} from "../../Resource/Contents";
import {
    boolParse,
    moneyParse,
    regularPaymentParse,
    singlePaymentParse,
    bankAccountParse,
    pensionArrangementTypeParse,
    IReviewProps,
    transferInParse,
    indexationParse,
    dateParser,
    currentValueParse
} from "../ReviewParser";
import { NewQuoteAction } from "../../../NewQuoteWizard/Actions/NewQuoteAction";
import { SubAccountTypeHelper } from "../../Helpers/SubAccountTypeHelper";
import { QuoteAndApplyBaseModel } from "../../../../../../../Wrap/Models/Models/QuoteAndApplyBase/QuoteAndApplyBaseModel";
import { JsonDateParser } from "../../../../../../../react-core/Utils/JsonDateParser";
import { getMoneyFormat } from "../../../NewQuoteWizard/Components/Shared/Utils";
import { ThirdPartyPaymentModel } from "../../../../../../../Wrap/Models/Models/Shared/ThirdPartyPaymentModel";
import { TitleMap } from "../../../../../../Shared/Entities/Maps/TitleMap";
import { isNullOrEmpty } from "../../../../../../../react-core/Utils/Arrays";
import { CedingSchemeTypesClient } from "../../../../../../../Wrap/Api/Transfer/CedingSchemeTypes";
import { PensionArrangementType } from "../../../../../../../Wrap/Constants/Enums/Domain/PensionArrangementType";
import { CountryCodeMap } from "../../../../../../Shared/Entities/Maps/CountryCodeMap";
import { TransferType } from "../../../../../../../Wrap/Constants/Enums/Domain/TransferType";
import { StringHelper } from "../../../../../../../react-core/Helpers/StringHelper";

export const PaymentsInReview = (props: IReviewProps) => {
    const { data, extraInfo, subAccountType, process } = props;
    const _retrieveCedingSchemeTypesClient: CedingSchemeTypesClient = new CedingSchemeTypesClient()

    const [cedingSchemeTypeOptions, setCedingSchemeTypeOptions] = useState([]);

    useEffect(() => {
        getCedingSchemeTypeOptions();
    }, []);

    const getCedingSchemeTypeOptions = () => {
        _retrieveCedingSchemeTypesClient.get()
            .onSuccess((results) => setCedingSchemeTypeOptions(results));
    };

    const singlePaymentTypeConfig = (paymentData: any, paymentType: PaymentType) => {
        if (!paymentData) { return false; }
        return { title: <strong>{paymentType} single payment</strong>, data: singlePaymentParse(paymentData, subAccountType) };
    };

    const bankAccountConfig = (bankAccountNumber: any) => {
        if (!bankAccountNumber) { return false; }

        return { title: "Bank account", data: bankAccountParse(bankAccountNumber, extraInfo) };
    };

    const paymentMethodConfig = (paymentMethod: any, isRegular: boolean) => {
        if (!paymentMethod) { return false; }
        return {
            title: "Payment method",
            data: isRegular ? regularDepositMethodArraysMap[paymentMethod] : depositMethodArraysMap[paymentMethod],
        };
    };

    const paymentStartConfig = (model: any) => {
        if (!model) { return false; }
        return {
            title: "Collection starts",
            data: JsonDateParser.getFormatDateString(
                new Date(model?.RegularBuyId ? model?.NextPaymentDate : model?.FirstCollectionDate),
                "dd MM yyyy",
                true
            ),
        };
    };

    const paymentEndConfig = (continueUntil: any) => {
        if (!continueUntil) { return false; }
        return {
            title: "Collection ends",
            data: JsonDateParser.getFormatDateString(
                new Date(continueUntil),
                "dd MM yyyy",
                true
            ),
        };
    }

    const regularPaymentTypeConfig = (paymentData: any, paymentType: PaymentType) => {
        if (!paymentData) { return false; }
        return { title: <strong>{paymentType} regular payment</strong>, data: regularPaymentParse(paymentData, subAccountType) };
    };

    const indexationConfig = (payment: any) => {
        if (!payment || SubAccountTypeHelper.isWrapCash(subAccountType) || SubAccountTypeHelper.isIsa(subAccountType) || SubAccountTypeHelper.isPP(subAccountType)) { return false; }
        return {
            title: "Indexation",
            data: indexationParse(payment.Indexation)};
    };
    const financialAdviceConfig = (advice:any) => {
        if(advice == null || advice == undefined){return false}
        return {
            title: "Financial advice",
            data: advice ? "Yes":"No"
        }
    };

    const ThirdPartyConfig = (thirdPartyPaymentModel: ThirdPartyPaymentModel) => {
        if (!thirdPartyPaymentModel) { return false; }
        return [
            { title: <div className="text-bold mt-10">Payer details</div>, data: " " },
            { title: "Third-party type", data: ThirdPartyTypeArrayMap[thirdPartyPaymentModel.Type] },
            {
                title: "Third-party name",
                data: `${TitleMap.getString(thirdPartyPaymentModel.Title)} ${thirdPartyPaymentModel.FirstName} ${thirdPartyPaymentModel.LastName
                    }`,
            },
            {
                title: "Date of birth",
                data: JsonDateParser.getFormatDate(thirdPartyPaymentModel.DateOfBirth, "dd/MM/yyyy"),
            },
            { title: "Address Line 1", data: thirdPartyPaymentModel.Address?.Line1 },
            { title: "Address Line 2", data: thirdPartyPaymentModel.Address?.Line2 },
            { title: "Address Line 3", data: thirdPartyPaymentModel.Address?.Line3 },
            { title: "Address Line 4", data: thirdPartyPaymentModel.Address?.Line4 },
            { title: "Postcode", data: thirdPartyPaymentModel.Address?.Postcode },
        ];
    };

    const employerDetailConfig = (payment) => {
        if (!payment || !SubAccountTypeHelper.isOnPlatformPension(subAccountType)) {
            return false;
        }

        const isNullOrEmpty = (value) => value == null || value === "";
        const { EmployerName, Address } = payment;
        const { CountryCode, Line1, Line2, Line3, Postcode } = Address || {};
        if (
            isNullOrEmpty(EmployerName) &&
            (isNullOrEmpty(CountryCode) || CountryCode === 0) &&
            isNullOrEmpty(Line1) &&
            isNullOrEmpty(Line2) &&
            isNullOrEmpty(Line3) &&
            isNullOrEmpty(Postcode)
        ) {
            return false;
        }

        return [
            { title: <div className="text-bold mt-10">Payer details</div>, data: " " },
            { title: "Employer name", data: payment.EmployerName },
            { title: "Country", data: CountryCodeMap.getString(payment.Address?.CountryCode ?? 0) },
            { title: "Address Line 1", data: payment.Address?.Line1 },
            { title: "Address Line 2", data: payment.Address?.Line2 },
            { title: "Address Line 3", data: payment.Address?.Line3 },
            { title: "Postcode", data: payment.Address?.Postcode },
        ];
    }

    const isAssumedShow = (): boolean => {
        if (process != ProcessType.Quote || !extraInfo) { return false; }
        switch (subAccountType) {
            case SubAccountType.Npr:
            case SubAccountType.Sipp:
                return true;
            case SubAccountType.StocksIsa:
                return !(extraInfo as QuoteAndApplyBaseModel).SelectedProducts.some((ele) =>
                    SubAccountTypeHelper.isOnPlatformPension(ele.SubAccountType)
                );

            case SubAccountType.InvestmentAccount:
                return (
                    !(extraInfo as QuoteAndApplyBaseModel).SelectedProducts.some((ele) =>
                        SubAccountTypeHelper.isOnPlatformPension(ele.SubAccountType)
                    ) &&
                    !(extraInfo as QuoteAndApplyBaseModel).SelectedProducts.some((ele) =>
                        SubAccountTypeHelper.isIsa(ele.SubAccountType)
                    )
                );
            default:
                return false;
        }
    };
    const getRetireAge = (model: QuoteAndApplyBaseModel): string => {
        const birthday = JsonDateParser.toDateNoOffset(model?.ClientDetails?.DateOfBirth);
        const plannedRetirementDate = JsonDateParser.toDateNoOffset(model?.AnnuityAssumptions?.PlannedRetirementDate);
        return plannedRetirementDate
            ? (plannedRetirementDate.getFullYear() - birthday.getFullYear()).toString()
            : "None";
    };

    const getAssumeAmount = (model: QuoteAndApplyBaseModel): string => {
        const Assumed = model?.AssumedHoldings;

        return Assumed == null ? "None" : moneyParse(model?.AssumedHoldings?.Amount);
    };

    const paymentsInfoConfig = (model: QuoteAndApplyBaseModel) => {
        if (!extraInfo) { return []; }
        const config = [
            process != ProcessType.SinglePayment &&
            process != ProcessType.AddRegular &&
            process != ProcessType.EditRegular &&
            process != ProcessType.TopUpWithITFLS &&
            SubAccountTypeHelper.isOnPlatformPension(subAccountType) && {
                title: "Selected retirement",
                data: getRetireAge(model),
            },
            isAssumedShow() && {
                title: "Assumed other holdings",
                data: getAssumeAmount(model),
            },
        ];
        return config.filter(Boolean);
    };

    const hasAnyProtection = () => {
        return data.PensionProtection?.HasAnyProtection;
    }

    const pensionProtectionConfig = () => {
        return [{
            title: "Pension Protection",
            data: hasAnyProtection() ? "Yes" : "No",
        }].filter(Boolean);
    }

    const annuityAssumptionsConfig = () => {
        const config = [];

        if (process == ProcessType.AddRegular || process == ProcessType.EditRegular) {
            config.push({
                title: "Did you give financial advice?",
                data: data?.AdviceGiven ? "Yes" : "No",
            });
        }

        config.push({
            title: "Edit annuity assumptions",
            data: data?.PensionAnnuityAssumption != null ? "Yes" : "No",
        });

        if (data?.PensionAnnuityAssumption != null) {
            if (process == ProcessType.NB) {
                config.push({
                    title: "Annuity payment in",
                    data: AnnuityPaymentsTypeOptionsMap[data?.PensionAnnuityAssumption?.PaymentInType],
                });

                config.push({
                    title: "Add spouses pension",
                    data: data?.PensionAnnuityAssumption?.IfAddSpousePension ? "Yes" : "No",
                });

                if (data?.PensionAnnuityAssumption?.IfAddSpousePension) {
                    config.push({
                        title: "Spouse's pension percentage",
                        data: data?.PensionAnnuityAssumption?.SpousePensionPercentage,
                    });
                    config.push({
                        title: "Spouse's Date of Birth",
                        data: JsonDateParser.getFormatDate(data?.PensionAnnuityAssumption?.SpouseDoB, "dd/MM/yyyy")
                    });
                    config.push({
                        title: "Spouse's Gender",
                        data: data?.PensionAnnuityAssumption?.SpouseGender,
                    });
                }
            }

            config.push({
                title: "Pension Increase",
                data: data?.PensionAnnuityAssumption?.IfRPI ? "RPI" : data?.PensionAnnuityAssumption?.PaymentIndexationLevel + "%",
            });

            config.push({
                title: "Guarantee period",
                data: data?.PensionAnnuityAssumption?.GuaranteePeriod + " years",
            });
        }

        return config.filter(Boolean);
    }

    const MPAAConfig = () => {        
        const config = [];
        if (data?.MoneyPurchaseAnnualAllowance?.Triggered != null) {
            const value =StringHelper.ConvertBooleanToString(data?.MoneyPurchaseAnnualAllowance?.Triggered);
            config.push({
                title: MpaaQuestionText,
                data: value,
            });            
        }
        return config;
    };

    const singlePaymentConfig = () => {
        let config = [];
        config.push(singlePaymentTypeConfig(data.ClientSinglePayment, PaymentType.Client));
        if (shouldDisplaySinglePaymentDetail()) {
            if (process != ProcessType.Quote && process != ProcessType.WhatIf) {
                config.push(paymentMethodConfig(data.ClientSinglePayment?.PaymentMethod, false));
            }
            config.push(bankAccountConfig(data.ClientSinglePayment?.BankAccountNumber));
        }

        config.push(singlePaymentTypeConfig(data.ThirdPartySinglePayment, PaymentType.ThirdParty));
        if (shouldDisplaySinglePaymentDetail()) {
            if (process != ProcessType.Quote && process != ProcessType.WhatIf) {
                config.push(paymentMethodConfig(data.ThirdPartySinglePayment?.PaymentMethod, false));
            }
            config.push(bankAccountConfig(data.ThirdPartySinglePayment?.BankAccountNumber));
            if (process != ProcessType.Quote) {
                config = config.concat(ThirdPartyConfig(data.ThirdPartySinglePayment));
            }
        }

        config.push(singlePaymentTypeConfig((data).EmployerSinglePayment, PaymentType.Employer));
        if (shouldDisplaySinglePaymentDetail()) {
            if (process != ProcessType.Quote && process != ProcessType.WhatIf) {
                config.push(paymentMethodConfig(data.EmployerSinglePayment?.PaymentMethod, false));
            }
            config.push(bankAccountConfig(data.EmployerSinglePayment?.BankAccountNumber));
            config = config.concat(employerDetailConfig(data?.EmployerSinglePayment));
        }

        config.push(
            singlePaymentTypeConfig(data.EmployerSalaryDeductionSinglePayment, PaymentType.EmployerSalaryDeduction)
        );
        if (shouldDisplaySinglePaymentDetail()) {
            if (process != ProcessType.Quote && process != ProcessType.WhatIf) {
                config.push(paymentMethodConfig(data.EmployerSalaryDeductionSinglePayment?.PaymentMethod, false));
            }
            config.push(bankAccountConfig(data.EmployerSalaryDeductionSinglePayment?.BankAccountNumber));
            config = config.concat(employerDetailConfig(data?.EmployerSalaryDeductionSinglePayment));
        }

        return config.filter(Boolean);
    };

    const regularPaymentConfig = () => {
        let config = [];

        config.push(regularPaymentTypeConfig(data.ClientRegularPayment, PaymentType.Client));
        if (shouldDisplayRegularPaymentDetail()) {
            config.push(paymentStartConfig(data?.ClientRegularPayment));
            config.push(paymentEndConfig(data.ClientRegularPayment?.ContinueUntil));
            config.push(paymentMethodConfig(data.ClientRegularPayment?.RegularPaymentMethod, true));
            config.push(bankAccountConfig(data.ClientRegularPayment?.BankAccountNumber));
        }
        config.push(indexationConfig(data?.ClientRegularPayment))

        config.push(regularPaymentTypeConfig(data.ThirdPartyRegularPayment, PaymentType.ThirdParty));
        if (shouldDisplayRegularPaymentDetail()) {
            config.push(paymentStartConfig(data?.ThirdPartyRegularPayment));
            config.push(paymentEndConfig(data.ThirdPartyRegularPayment?.ContinueUntil));
            config.push(paymentMethodConfig(data.ThirdPartyRegularPayment?.RegularPaymentMethod, true));
            config.push(bankAccountConfig(data.ThirdPartyRegularPayment?.BankAccountNumber));
            config = config.concat(ThirdPartyConfig(data.ThirdPartyRegularPayment));
        }
        config.push(indexationConfig(data?.ThirdPartyRegularPayment))


        config.push(regularPaymentTypeConfig(data.EmployerRegularPayment, PaymentType.Employer));
        if (shouldDisplayRegularPaymentDetail()) {
            config.push(paymentStartConfig(data?.EmployerRegularPayment));
            config.push(paymentEndConfig(data.EmployerRegularPayment?.ContinueUntil));
            config.push(paymentMethodConfig(data.EmployerRegularPayment?.RegularPaymentMethod, true));
            config.push(bankAccountConfig(data.EmployerRegularPayment?.BankAccountNumber));
            config.push(indexationConfig(data?.EmployerRegularPayment));
            config = config.concat(employerDetailConfig(data?.EmployerRegularPayment));
        } else {
            config.push(indexationConfig(data?.EmployerRegularPayment));
        }

        config.push(
            regularPaymentTypeConfig(data.EmployerSalaryDeductionRegularPayment, PaymentType.EmployerSalaryDeduction)
        );
        if (shouldDisplayRegularPaymentDetail()) {
            config.push(paymentStartConfig(data?.EmployerSalaryDeductionRegularPayment));
            config.push(paymentEndConfig(data.EmployerSalaryDeductionRegularPayment?.ContinueUntil));
            config.push(paymentMethodConfig(data.EmployerSalaryDeductionRegularPayment?.RegularPaymentMethod, true));
            config.push(bankAccountConfig(data.EmployerSalaryDeductionRegularPayment?.BankAccountNumber));
            config.push(indexationConfig(data?.EmployerSalaryDeductionRegularPayment));
            config = config.concat(employerDetailConfig(data?.EmployerSalaryDeductionRegularPayment));
        } else {
            config.push(indexationConfig(data?.EmployerSalaryDeductionRegularPayment));
        }
        
        if (process != ProcessType.WhatIf) {
            config.push(financialAdviceConfig(data?.AdviceGiven));    
        }
        
        return config.filter(Boolean);
    };

    const shouldDisplaySinglePaymentDetail = () => {
        return (
            process == ProcessType.NB || process == ProcessType.SinglePayment ||
            process == ProcessType.TopUpWithITFLS || process == ProcessType.Quote || process == ProcessType.WhatIf
        );
    };

    const shouldDisplayRegularPaymentDetail = () => {
        return process == ProcessType.NB || process == ProcessType.EditRegular || process == ProcessType.AddRegular || process == ProcessType.WhatIf;
    };

    const shouldDisplayAnnuityAssumptionsDetail = () => {
        return process != ProcessType.Quote && SubAccountTypeHelper.isOnPlatformPension(subAccountType);
    };

    const shouldDisplayOPPTransferDetail = () => {
        return !SubAccountTypeHelper.isPPAndISA(subAccountType)
    };

    const transferInConfig = () => {
        const config = [];
        const addExtraInfo =
            shouldDisplaySinglePaymentDetail() && SubAccountTypeHelper.isOnPlatformPension(subAccountType);

        !isNullOrEmpty(transferInParse(data, subAccountType)) &&
            transferInParse(data, subAccountType).map((ele, index) => {
                if (process == ProcessType.TopUpWithITFLS || process == ProcessType.SinglePayment || process == ProcessType.NB || process == ProcessType.NBEC) {                                                            
                    config.push({ title: <strong>Transfer in  {index + 1}</strong>, data: " " });
                    if (shouldDisplayOPPTransferDetail()) {
                        config.push({
                            title: "Pension source",
                            data: pensionArrangementTypeParse(ele?.data?.PensionSource),
                        });
                    }

                    config.push({
                        title: "Drawdown type",
                        data: DrawdownTypeMap[ele?.data?.DrawdownType]
                    });

                    config.push({
                        title: "Ceding scheme",
                        data: ele?.data?.ProviderDetails?.Name,
                    });
                    config.push({
                        title: "Ceding type",
                        data: cedingSchemeTypeOptions?.find(x => x.Id == ele?.data?.PensionDetails?.ExternalPensionSchemeType)?.DisplayName,
                    });
                    if (shouldDisplayOPPTransferDetail()) {
                        config.push({
                            title: "Block transfer",
                            data: boolParse(ele?.data?.IsBlockTransfer),
                        });
                    }
                    if( ele?.data?.PensionSource != PensionArrangementType.Accumulation && shouldDisplayOPPTransferDetail()){
                        config.push({
                            title: "Dependant's drawdown",
                            data: boolParse(ele?.data?.PensionDetails?.IsDependentsPension),
                        });
                    }
                    config.push({
                        title: "Account number",
                        data: ele?.data?.AccountOrPlanNumber,
                    });
                    config.push({
                        title: "Transfer type",
                        data: ele?.data?.TransferType == TransferType.Cash
                            ? "Cash"
                            : subAccountType == SubAccountType.StocksIsa
                                ? "S&S ISA"
                                : "Re-register assets",
                    });
                    config.push({
                        title: "Current value",
                        data: getMoneyFormat(currentValueParse(ele?.data)),
                    });
                    if (shouldDisplayOPPTransferDetail()) {
                        config.push({
                            title: "Full or partial transfer",
                            data: ele?.data?.FullTransfer ? "Full" : "Partial",
                        });
                    }
                } else {
                    config.push({ title: <strong>Transfer in  {index + 1}</strong>, data: ele?.jsx });
                    if (addExtraInfo) {
                        config.push({
                            title: "Pension source",
                            data: pensionArrangementTypeParse(ele?.data?.PensionSource),
                        });
                        if (ele?.data?.PensionSource != PensionArrangementType.Accumulation) {
                            config.push({
                                title: "Dependant's pension",
                                data: boolParse(ele?.data?.PensionDetails?.IsDependentsPension),
                            });
                        }
                        if (subAccountType != SubAccountType.JuniorSipp) {
                            config.push({
                                title: DpcQuestionText,
                                data: boolParse(ele?.data?.PensionDetails?.IsDisqualifyingPensionCredits),
                            });
                        };
                        config.push({
                            title: "Drawdown type",
                            data: DrawdownTypeMap[ele?.data?.DrawdownType]
                        });

                        if (ele?.data?.PensionSource !== PensionArrangementType.Accumulation) {
                            config.push({
                                title: "GAD maximum",
                                data: getMoneyFormat(ele?.data?.IncomePaymentDetails?.GadMaximum?.Amount),
                            });
                            config.push({
                                title: "Income taken to date",
                                data: getMoneyFormat(ele?.data?.IncomePaymentDetails?.CurrentYearIncome?.Amount),
                            });
                            config.push({
                                title: "Last mandatory review date",
                                data: dateParser(ele?.data?.IncomePaymentDetails?.LastGadReviewDate),
                            });
                        }
                    }
                }
            });
        return config;
    };

    const extraInfoConfig = () => {
        const config = [];
        if (!extraInfo) { return config; }
        if (process == ProcessType.NB && SubAccountTypeHelper.isOnPlatformPension(subAccountType)) {

            config.push({
                title: "Source of wealth",
                data: SourceOfWealthArrayMap[data?.SourceOfWealth?.SourceOfWealthOption],
            });
            if (data?.SourceOfWealth?.SourceOfWealthOption == SourceOfWealthOption.NotSpecified) {
                config.push({
                    title: "Other source of wealth",
                    data: data.SourceOfWealth.OtherSourceOfWealth,
                });
            }
        }
        return config;
    };

    const navigate = () => {
        if (process == ProcessType.NB) {
            Navigator.navigateToAndPreserveQueryParameters(paymentsInUrlConfig());
        } else if (process == ProcessType.Quote) {
            paymentsInUrlConfigForNewQuote();
        } else if (process == ProcessType.SinglePayment) {
            Navigator.navigateToAndPreserveQueryParameters(Urls.get().SPTPaymentsIn);
        } else if (process == ProcessType.TopUpWithITFLS) {
            Navigator.navigateToAndPreserveQueryParameters(Urls.get().TopUpWithITFLSPaymentsIn);
        } else if (process == ProcessType.AddRegular || ProcessType.EditRegular) {
            Navigator.navigateToAndPreserveQueryParameters(Urls.get().RegularPaymentsIn);
        }
    };

    const paymentsInUrlConfigForNewQuote = () => {
        switch (subAccountType) {
            case SubAccountType.InvestmentAccount:
                return NewQuoteAction.copyQuoteInstruction(Urls.get().QuotePaymentsInPP);
            case SubAccountType.StocksIsa:
                return NewQuoteAction.copyQuoteInstruction(Urls.get().QuotePaymentsInISA);
            case SubAccountType.Pension:
            case SubAccountType.Sipp:
            case SubAccountType.Npr:
                return NewQuoteAction.copyQuoteInstruction(Urls.get().QuotePaymentsInSIPP);
            case SubAccountType.JuniorSipp:
                return NewQuoteAction.copyQuoteInstruction(Urls.get().QuotePaymentsInJSIPP);
            default:
                break;
        }
    };
    const paymentsInUrlConfig = () => {
        switch (subAccountType) {
            case SubAccountType.InvestmentAccount:
                return Urls.get().NBPaymentsInPP;
            case SubAccountType.WrapCash:
                return Urls.get().NBPaymentsInWrapCash;
            case SubAccountType.StocksIsa:
                return Urls.get().NBPaymentsInISA;
            case SubAccountType.Pension:
            case SubAccountType.Sipp:
            case SubAccountType.Npr:
                return Urls.get().NBPaymentsInSIPP;
            case SubAccountType.JuniorSipp:
                return Urls.get().NBPaymentsInJSIPP;
            default:
                break;
        }
    };

    return (
        <>
            {
                <Accordion
                    label="Payments in"
                    isOpen={props.isOpen != null ? props.isOpen : false}
                    onEdit={() => (props.onEditAction ? props.onEditAction() : navigate())}
                >
                    <div className="pl-34">
                        <Typography type="common" config={paymentsInfoConfig(extraInfo)} colNum={1}></Typography>
                        {hasAnyProtection() && <Typography type="common" config={pensionProtectionConfig()} colNum={1}></Typography>}
                        {shouldDisplayAnnuityAssumptionsDetail() && (
                            <Typography type="common" config={annuityAssumptionsConfig()} colNum={1}></Typography>
                        )}
                        <Typography type="common" config={MPAAConfig()} colNum={1}></Typography>
                        {shouldDisplaySinglePaymentDetail() && singlePaymentConfig().length > 0 && (<h3>Single payment</h3>)}
                        <Typography type="common" config={singlePaymentConfig()} colNum={1}></Typography>

                        {shouldDisplayRegularPaymentDetail() && regularPaymentConfig().length > 0 && (<h3>Regular payment</h3>)}
                        {(shouldDisplayRegularPaymentDetail() || props.process == ProcessType.Quote) && (
                            <Typography type="common" config={regularPaymentConfig()} colNum={1}></Typography>
                        )}
                        {!isNullOrEmpty(transferInParse(data, subAccountType)) && <h3>Transfer in</h3>}
                        <Typography type="common" config={transferInConfig()} colNum={1}></Typography>
                        {SubAccountTypeHelper.isOnPlatformPension(subAccountType) && (
                            <Typography type="common" config={extraInfoConfig()} colNum={1}></Typography>
                        )}
                    </div>
                </Accordion>
            }
        </>
    );
};
